import z from "zod";
import { AuditMetaSchema } from "./auditMetaTypes";
import { TenancySchema } from "./tenancyTypes";
import { BoundaryIdSchema } from "./idTypes";
import { RoleNameSchema, type RoleName } from "./userContextTypes";

export const RoleValues = ["role.collaborator", "role.reader", "role.owner"] as const;
export const RoleSchema = z.enum(RoleValues);
export type Role = z.infer<typeof RoleSchema>;

// Grant operations that can be performed on resources
export const GrantOpSchema = z.enum(["op.create", "op.delete", "op.read", "op.update"]);
export type GrantOp = z.infer<typeof GrantOpSchema>;

// Grants map showing which operations are allowed for a resource
export const GrantsSchema = z.record(GrantOpSchema, z.boolean()).optional();
export type Grants = z.infer<typeof GrantsSchema>;

// Re-export for convenience
export type { RoleName };

// Domain request for invitations
export const DomainRequestSchema = z.object({
  name: z.string(),
});
export type DomainRequest = z.infer<typeof DomainRequestSchema>;

// Invited user request for invitations
export const InvitedUserRequestSchema = z.object({
  email: z.string(),
});
export type InvitedUserRequest = z.infer<typeof InvitedUserRequestSchema>;

// Create invitation request
export const CreateInvitationRequestSchema = z.object({
  domains: z.array(DomainRequestSchema).optional(),
  role: RoleNameSchema,
  users: z.array(InvitedUserRequestSchema).optional(),
});
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequestSchema>;

// Update invitation request
export const UpdateInvitationRequestSchema = z.object({
  domains: z.array(DomainRequestSchema).optional(),
  role: RoleNameSchema,
  users: z.array(InvitedUserRequestSchema).optional(),
});
export type UpdateInvitationRequest = z.infer<typeof UpdateInvitationRequestSchema>;

// Domain (full object returned from backend)
export const DomainSchema = z.object({
  id: BoundaryIdSchema.optional(),
  invitationId: BoundaryIdSchema.optional(),
  name: z.string(),
});
export type Domain = z.infer<typeof DomainSchema>;

// Identifier for invited user
export const IdentifierSchema = z.object({
  id: z.string(), // This is the email
  resource: z.string(),
});
export type Identifier = z.infer<typeof IdentifierSchema>;

// Invited user (full object returned from backend)
export const InvitedUserSchema = z.object({
  id: BoundaryIdSchema.optional(),
  invitationId: BoundaryIdSchema.optional(),
  identifier: IdentifierSchema,
});
export type InvitedUser = z.infer<typeof InvitedUserSchema>;

// Invitation link
export const InvitationLinkSchema = z.object({
  token: z.string().optional(),
  url: z.string().optional(),
  destination: z.string().optional(),
});
export type InvitationLink = z.infer<typeof InvitationLinkSchema>;

// Resource enum (optional, might be needed)
export const ResourceSchema = z.string().optional();
export type Resource = z.infer<typeof ResourceSchema>;

// Full invitation object returned from backend
export const InvitationSchema = z
  .object({
    id: BoundaryIdSchema,
    role: RoleNameSchema,
    resource: ResourceSchema,
    resourceId: z.string().optional(),
    domains: z.array(DomainSchema).optional().nullable(),
    humans: z.array(InvitedUserSchema).optional().nullable(),
    link: InvitationLinkSchema,
    grants: GrantsSchema,
  })
  .extend(TenancySchema.shape)
  .extend(AuditMetaSchema.shape);
export type Invitation = z.infer<typeof InvitationSchema>;

// Create invitations response - returns an array of created invitations
export const CreateInvitationsResponseSchema = z.array(InvitationSchema);
export type CreateInvitationsResponse = z.infer<typeof CreateInvitationsResponseSchema>;

// Invitations sync response - returns a map of invitation IDs to invitation objects
export const InvitationsSyncSchema = z.object({
  invites: z.record(BoundaryIdSchema, InvitationSchema),
});
export type InvitationsSync = z.infer<typeof InvitationsSyncSchema>;
